import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from "@material-ui/core/styles";
import {useHistory, useParams} from 'react-router-dom';

import apiFetch,  {getData } from '../utils/apiUtils';
import { ErrorContext, NotificationContext } from "../utils/constants";
import EnhancedTextInput from '../components/EnhancedTextInput';
import { RequestApprovalButton,
  ApprovalRequestedButton,
  ApproveOrReviseButtons,
  ApprovalGranted } from '../components/ApprovalButtons';
import { BodyText, SubsectionHeader } from '../components/Typography';

import { canEditInitiative } from '../utils/initiatives';

const useStyles = makeStyles(() => ({
  h1: {
    marginBottom: '5px'
  },
  commandText: {
    fontSize: '20px'
  },
  main: {
    width: '100%'
  },
  error: {
    display: 'none',
    color: 'red',
    fontSize: '20px',
    paddingTop: '1rem'
  },
  splitPane: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%'
  },
  dataContainer: {
    paddingRight: '2rem',
    width: '66%',
  },
  helpContainer: {
    padding: '5%',
    width: '33%',
    background: 'rgba(110, 247, 138, 0.1)',
  },
  createBtn: {
    marginRight: '1rem'
  },
  cardDiv: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    marginTop: '1rem',
    flexWrap: 'wrap',
    justifyContent: 'space-between'
  },
  card: {
    width: '32%',
    marginBottom: '1rem'
  },
  cardTitle: {
    fontSize: 14,
  },
  cardPos: {
    marginBottom: 12,
  },
  approvalHeaderContainer: {
    marginTop: '2rem',
  }
}));

const helpText = 'What is the main key performance indicator (KPI) that will change as a result of completing this project? Pick something that we can measure to determine project success.';

export default function KPISetup() {
  const classes = useStyles();
  const params = useParams();
  const history = useHistory();
  const { triggerError } = useContext(ErrorContext);

  // State
  const [ createBtnDisabled, setCreateBtnDisabled ] = useState(false);
  const [ unit_measure, setUnit_measure ] = useState('');
  const [ baseline, setBaseline ] = useState('');
  const [ target, setTarget ] = useState('');
  const [ kpis, setKpis ] = useState([]);
  const [ currentKpi, setCurrentKpis ] = useState([]);
  const [ validate, setValidate ] = useState(false);
  const [ initiative, setInitiative ] = useState({ _links: {} });
  const [ initiativeTeam, setInitiativeTeam ] = useState([]);
  const notification = useContext(NotificationContext);

  useEffect(() => {
    document.title = 'Rise by TransAlta Initiative KPI';
  }, []);

  const onError = () => triggerError({
    title: 'Communication Error',
    description: 'We were unable to read your KPI data from the server. Please try again.',
    onRetry: () => window.location.reload(),
  });

  const handleInitiativeError = (error) => {
    if (error.status === 404) {
      history.push('/ui/errors/error404');
    } else {
      onError();
    }
  };

  const fetchInitiative = () => getData('/initiatives/' + params.id, setInitiative, handleInitiativeError);

  useEffect(() => {
    fetchInitiative();
  }, [params.id]);

  useEffect(() => {
    if (initiative && initiative.id) {
      getData('/initiatives/' + params.id +'/kpis', setKpiValues, onError);
      getData('/initiatives/' + params.id + '/team', setInitiativeTeam, onError);
    }
  }, [params.id, initiative]);

  useEffect(() => {
    const isSupportedNotification = (notification && notification.initiativeId === params.id);
    if (!!isSupportedNotification) {
      getData('/initiatives/' + params.id +'/kpis', setKpiValues, onError);
      getData('/initiatives/' + params.id + '/team', setInitiativeTeam, onError);
      fetchInitiative();
    }
  },[notification]);

  // Methods
  const setKpiValues = data => {
    setKpis(data);
    if (data.length > 0) {
      const kpi = data[0];
      setCurrentKpis(kpi);
      setUnit_measure(kpi.unit_measure ? kpi.unit_measure : '');
      setBaseline(kpi.baseline ? kpi.baseline: '');
      setTarget(kpi.target ? kpi.target: '');
    } else {
      setCreateBtnDisabled(true);
    }
  };

  const handleFieldChange = (field) => (value) => {
    field(value);
  };

  const updateKpi = () => {
    if (!unit_measure || !baseline || !target) {
      setValidate(true);
    } else {
      setValidate(false);
    }
    if (currentKpi && currentKpi.id) {
      const kpiData = Object.assign({}, currentKpi);
      kpiData.unit_measure = unit_measure;
      kpiData.baseline = baseline;
      kpiData.target = target;
      apiFetch('/initiatives/kpi', 'PUT', JSON.stringify(kpiData),
        (data) => {
          const newKpis = [];
          newKpis.push (kpiData);
          kpiData.id = data.id;
          setKpis(newKpis);
        },
        () => triggerError({
            title: 'Update Error',
            description: `We were unable to update your KPI. Please try again.`,
            onRetry: updateKpi
          }));
    }
  };

  const disabled = () => {
    return !canEditInitiative(initiativeTeam) || (currentKpi && currentKpi.send_for_approval);
  };

  const approvalHeader = () => {
    return !!initiative.hasKpis && (
      <div className={classes.approvalHeaderContainer}>
        <SubsectionHeader>
          KPI Checkpoint
        </SubsectionHeader>
        <BodyText>
          {(initiative._links['KPI_WSL_APPROVAL'] || initiative._links['KPI_TO_APPROVAL']) ?
            'Review the KPI, then either approve it or request revisions.' :
            'When you’ve set up a KPI to identify the change created by your initiative, have it validated before proceeding.'
          }
        </BodyText>
      </div>
    );
  };

  const getTopBtn = () => {
    if (kpis.length === 0 || !kpis[0].unit_measure || !kpis[0].baseline || !kpis[0].target) {
      return '';
    }
    if (initiative._links['KPI_WSL_APPROVAL']) {
      return <ApproveOrReviseButtons
        approvalUrl={initiative._links['KPI_WSL_APPROVAL'].href}
        onSuccess={fetchInitiative}
        approveText='APPROVE'
        reviseText='REJECT'
      />
    }
    if (initiative._links['KPI_TO_APPROVAL']) {
      return <ApproveOrReviseButtons
        approvalUrl={initiative._links['KPI_TO_APPROVAL'].href}
        onSuccess={fetchInitiative}
        approveText='APPROVE'
        reviseText='REJECT'
      />
    }
    if (initiative._links['KPI_REQUEST_APPROVAL']) {
      return <RequestApprovalButton
        approvalUrl={initiative._links['KPI_REQUEST_APPROVAL'].href}
        onSuccess={fetchInitiative}
      />;
    }
    if (initiative.kpi_request_user != null) {
      return ((initiative.kpi_wsl_user == null) && (initiative.kpi_to_user == null)) ?
        <ApprovalRequestedButton/> :
        <ApprovalGranted
          approvalType='KPI'
          approverNames={[initiative.kpi_wsl_user || initiative.kpi_to_user]}
        />;
    }
  };


  const regexValidatorCurrency = /^-?[\d\,]{0,15}(\.\d{0,2})?$/;

  return (
    <main className={classes.main}>
      <h1 className={classes.h1}>Measuring your initiative's success</h1>
      Set up a KPI to validate the change made by your initiative.



      <p className={classes.commandText}>Set a KPI</p>
      <div className={classes.splitPane}>
        <div className={classes.dataContainer}>
          What is the metric we can use to measure the change created by this initiative?
          <EnhancedTextInput
            maxText={255}
            validate={validate}
            changeMethod={handleFieldChange(setUnit_measure)}
            placeholder="Enter unit of measure..."
            initialValue={unit_measure}
            autoFocus={true}
            disabled={disabled()}
            onBlurHandler={updateKpi}
          />
          What is the value of this performance metric before doing this initiative?
          <EnhancedTextInput
            maxText={18}
            validate={validate}
            changeMethod={handleFieldChange(setBaseline)}
            placeholder="Baseline value.."
            initialValue={baseline}
            regexValidation={regexValidatorCurrency}
            disabled={disabled()}
            onBlurHandler={updateKpi}
          />
          What is the the target value you hope to achieve by doing this initiative?
          <EnhancedTextInput
            maxText={18}
            validate={validate}
            changeMethod={handleFieldChange(setTarget)}
            placeholder="Target Value..."
            initialValue={target}
            regexValidation={regexValidatorCurrency}
            disabled={disabled()}
            onBlurHandler={updateKpi}
          />
        </div>
        <div className={classes.helpContainer}>
          {helpText}
        </div>
      </div>
      { approvalHeader() }
      { getTopBtn() }
    </main>
  );
}
